// SPDX-License-Identifier: MIT

import { getSuccessAnimation } from '../common/theme';
import { escapeHtml } from '../common/utils';
import { IRenderOptions } from '../base/render-options';

export interface RenderZapButtonOptions extends IRenderOptions {
  isAmountLoading: boolean;
  isSuccess: boolean;
  buttonText: string;
  totalZapAmount: number | null;
  hasZaps?: boolean;
}

export function renderZapButton({
  isLoading,
  isError,
  isSuccess,
  errorMessage,
  buttonText,
  totalZapAmount,
  isAmountLoading,
  hasZaps = false,
}: RenderZapButtonOptions): string {

  if (isError) {
    return renderError(errorMessage || '');
  }

  if (isLoading) {
    return renderLoading(isAmountLoading);
  }

  const iconContent = isSuccess
    ? getSuccessAnimation('light')
    : getLightningIcon();
  const textContent = isSuccess
    ? `<span>Zapped</span>`
    : `<span>${escapeHtml(buttonText)}</span>`;

  return renderContainer(iconContent, textContent, totalZapAmount, isAmountLoading, hasZaps);
}

function renderLoading(isAmountLoading: boolean): string {
  return renderContainer(
    getLightningIcon(),
    '<span class="button-text-skeleton"></span>',
    null,
    isAmountLoading
  );
}

function renderError(errorMessage: string): string {
  return renderErrorContainer(
    '<div class="error-icon">&#9888;</div>',
    escapeHtml(errorMessage)
  );
}

function renderErrorContainer(leftContent: string, rightContent: string): string {
  return `
    <div class="nostr-zap-button-container">
      <div class="nostr-zap-button-left-container">
        ${leftContent}
      </div>
      <div class="nostr-zap-button-right-container">
        ${rightContent}
      </div>
    </div>
  `;
}

function renderContainer(iconContent: string, textContent: string, totalZapAmount: number | null, isAmountLoading: boolean, hasZaps: boolean = false): string {
  const zapAmountHtml = isAmountLoading 
    ? `<span class="total-zap-amount skeleton"></span>` 
    : (totalZapAmount !== null ? `<span class="total-zap-amount${hasZaps ? ' clickable' : ''}">${totalZapAmount.toLocaleString()} ⚡ sats received</span>` : '');
  
  const helpIconHtml = `<button class="help-icon" title="What is a zap?">?</button>`;
  
  return `
    <div class="nostr-zap-button-container">
      <button class="nostr-zap-button">
        ${iconContent}
        ${textContent}
      </button>
      ${zapAmountHtml} ${helpIconHtml}
    </div>
  `;
}

function getLightningIcon(): string {
  // Yellow lightning regardless of text color
  return `<svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M13 2L3 14h7v8l10-12h-7z" fill="#FFC800"/></svg>`;
}
